import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import ScrollToTop from '../components/ScrollToTop';
import { 
  Zap, 
  Smartphone, 
  Wifi, 
  Edit3, 
  Users, 
  TrendingUp,
  Home,
  Camera,
  Utensils,
  Building2,
  Briefcase,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  Star,
  ShoppingBag,
  CreditCard,
  Tag,
  Package,
  Palette,
  Play
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import ProductShowcase from '../components/ProductShowcase';
import ScrollReveal from '../components/ScrollReveal';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState({});
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  
  // Scroll to hash anchor when navigating from other pages
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Zap,
      title: 'Instant Tap Sharing',
      description: 'Share your contact info in less than a second with just a tap'
    },
    {
      icon: Smartphone,
      title: 'No App Required',
      description: 'Works natively with all modern smartphones - no downloads needed'
    },
    {
      icon: Wifi,
      title: 'Works With iPhone & Android',
      description: 'Universal NFC technology compatible with all devices'
    },
    {
      icon: Edit3,
      title: 'Custom Branding',
      description: 'Personalize with your logo, colors, and brand identity'
    },
    {
      icon: TrendingUp,
      title: 'Reusable & Editable',
      description: 'Update your information anytime without reprinting'
    },
    {
      icon: Users,
      title: 'Perfect For Networking',
      description: 'Never lose a connection - instant digital business card exchange'
    }
  ];

  const useCases = [
    {
      icon: Home,
      title: 'Realtors',
      description: 'Share property listings instantly at open houses',
      image: 'https://images.unsplash.com/photo-1770199105692-9e52ff137cad'
    },
    {
      icon: Camera,
      title: 'Influencers',
      description: 'Connect followers to all your social profiles in one tap',
      image: 'https://customer-assets.emergentagent.com/job_tap-connect-shop/artifacts/yn13hvmt_image.png'
    },
    {
      icon: Briefcase,
      title: 'Business Owners',
      description: 'Replace traditional cards with smart digital networking',
      image: 'https://images.unsplash.com/photo-1647580427159-c213e7f77878'
    },
    {
      icon: Users,
      title: 'Events',
      description: 'Streamline attendee networking and contact collection',
      image: 'https://images.unsplash.com/photo-1550305080-4e029753abcf'
    },
    {
      icon: Utensils,
      title: 'Restaurants',
      description: 'Share menus, reviews, and reservation links instantly',
      image: 'https://images.unsplash.com/photo-1758887261865-a2b89c0f7ac5'
    },
    {
      icon: Building2,
      title: 'Agencies',
      description: 'Professional client presentations and portfolio sharing',
      image: 'https://images.unsplash.com/photo-1675716921224-e087a0cca69a'
    },
    {
      icon: Users,
      title: 'Networking',
      description: 'Never miss a connection at conferences and meetups',
      image: 'https://images.unsplash.com/photo-1515169067868-5387ec356754'
    },
    {
      icon: BarChart3,
      title: 'Sales Teams',
      description: 'Track engagement and follow up with analytics',
      image: 'https://images.pexels.com/photos/6931202/pexels-photo-6931202.jpeg'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Real Estate Agent',
      content: 'This replaced all my business cards instantly. I close more deals because clients have all my info saved.',
      rating: 5,
      avatar: 'SM'
    },
    {
      name: 'Marcus Chen',
      role: 'Startup Founder',
      content: 'The analytics feature is incredible. I know exactly who engaged with my profile and can follow up strategically.',
      rating: 5,
      avatar: 'MC'
    },
    {
      name: 'Susan Lopez',
      role: 'Marketing Director',
      content: 'Our entire team uses these. Professional, modern, and they actually work. Best networking investment we\'ve made.',
      rating: 5,
      avatar: 'SL'
    }
  ];

  const faqs = [
    {
      question: 'Does it work on iPhone?',
      answer: 'Yes! All iPhones from iPhone 7 and newer support NFC and work seamlessly with our products. No app required.'
    },
    {
      question: 'Do users need an app?',
      answer: 'No app needed! When someone taps your NFC product, their phone automatically opens your profile in their default browser.'
    },
    {
      question: 'Can I update info later?',
      answer: 'Absolutely! Update your contact info, links, and profile anytime from our dashboard. Changes are instant - no reprinting needed.'
    },
    {
      question: 'Do you offer bulk pricing?',
      answer: 'Yes! We offer significant discounts for bulk orders. Contact our sales team for custom pricing on orders of 50+ units.'
    },
    {
      question: 'Can I customize branding?',
      answer: 'Yes! Add your logo, choose colors, and customize the design to match your brand. Custom branding available on all products.'
    }
  ];

  return (
    <div className="homepage">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold text-white flex items-center gap-2">
              <Wifi className="w-7 h-7 text-blue-500" />
              ShopNFCTags
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/products')} className="text-gray-300 hover:text-white transition-colors">Shop</button>
              <a href="#how-it-works" className="text-gray-300 hover:text-white transition-colors">How It Works</a>
              <a href="#use-cases" className="text-gray-300 hover:text-white transition-colors">Use Cases</a>
              <button onClick={() => navigate('/bulk-orders')} className="text-gray-300 hover:text-white transition-colors">Bulk Orders</button>
              <a href="#custom" className="text-gray-300 hover:text-white transition-colors">Custom</a>
              <button onClick={() => navigate('/contact')} className="text-gray-300 hover:text-white transition-colors">Contact</button>
            </div>
            <Button 
              onClick={() => navigate('/products')}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              Shop Now
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section with Scroll Animation */}
      <HeroSection />

      {/* Trust Bar */}
      <section className="py-8 bg-gradient-to-b from-black to-zinc-900 border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: Zap, text: 'Fast Shipping' },
              { icon: Smartphone, text: 'Works With iPhone & Android' },
              { icon: Wifi, text: 'No App Required' },
              { icon: Edit3, text: 'Custom Branding Available' },
              { icon: TrendingUp, text: 'Bulk Discounts' }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center gap-2">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-blue-500" />
                </div>
                <p className="text-sm text-gray-300">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is NFC */}
      <section id="what-is-nfc" className="py-24 bg-zinc-900" data-animate>
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-6">
              What is an NFC Smart Keychain?
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16 max-w-3xl mx-auto">
              Near Field Communication technology that lets you share your digital profile instantly - no apps, no QR codes, just tap and connect.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: '1', title: 'Tap Phone', description: 'Simply tap your phone to the NFC keychain, card, or tag', icon: Smartphone },
              { step: '2', title: 'Info Opens Instantly', description: 'Your digital profile opens automatically in their browser', icon: Zap },
              { step: '3', title: 'Save Contact', description: 'They save your contact info with one click - done!', icon: CheckCircle2 }
            ].map((step, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="bg-zinc-800/50 border-zinc-700 hover:border-blue-500/50 transition-all duration-300 hover:scale-105">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-6">
                      <step.icon className="w-8 h-8 text-blue-500" />
                    </div>
                    <div className="text-5xl font-bold text-blue-500 mb-4">{step.step}</div>
                    <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-gray-400">{step.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-24 bg-black" data-animate>
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-16">
              Why Choose NFC Smart Products?
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <ScrollReveal key={index} delay={index * 50}>
                <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 border-zinc-700 hover:border-blue-500/50 transition-all duration-300 hover:scale-105">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6">
                      <feature.icon className="w-7 h-7 text-blue-500" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <ProductShowcase />

      {/* Use Cases */}
      <section id="use-cases" className="py-24 bg-black overflow-visible" data-animate>
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-6">
              Perfect For Every Professional
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16 max-w-3xl mx-auto">
              From real estate to restaurants, NFC technology transforms how professionals connect and share information.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <ScrollReveal key={index} delay={index * 50}>
                <Card className="group bg-zinc-900 border-zinc-800 hover:border-blue-500/50 transition-all duration-300 overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={useCase.image} 
                      alt={useCase.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                      <useCase.icon className="w-6 h-6 text-blue-500" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{useCase.title}</h3>
                    <p className="text-gray-400 text-sm">{useCase.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Detail */}
      <section id="how-it-works" className="py-24 bg-gradient-to-b from-zinc-900 to-black overflow-visible" data-animate>
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-16">
              How It Works
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <ScrollReveal>
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-4 bg-blue-500/20 blur-3xl rounded-full"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_tap-connect-shop/artifacts/ixkwohv7_nfckeychaintap.png"
                  alt="NFC Technology Demo"
                  className="relative rounded-2xl shadow-2xl w-full h-auto max-h-96 object-cover"
                />
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="space-y-8">
                {[
                  { num: '01', title: 'Tap NFC Tag', desc: 'Customer taps their phone to your NFC keychain, card, or sticker' },
                  { num: '02', title: 'Profile Opens', desc: 'Your custom digital profile opens instantly in their browser' },
                  { num: '03', title: 'Customer Saves Info', desc: 'They save your contact, visit your links, or take action immediately' }
                ].map((step, index) => (
                  <div key={index} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                        {step.num}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <p className="text-gray-400">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-black overflow-visible" data-animate>
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-3">
              See NFC in Action
            </h2>
            <p className="text-lg text-gray-400 text-center mb-8 max-w-2xl mx-auto">
              Watch how easy it is to share your information with just a tap
            </p>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <div className="max-w-xl mx-auto">
              {/* Video Thumbnail with Play Button */}
              <div 
                className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-zinc-800 hover:border-blue-500/50 transition-all duration-300 cursor-pointer group"
                onClick={() => setIsVideoOpen(true)}
              >
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-20 group-hover:opacity-40 blur-xl transition-opacity"></div>
                
                {/* Thumbnail image */}
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_tap-connect-shop/artifacts/dvoe6ugz_single%20tap.webp"
                    alt="Play NFC Demo Video"
                    className="w-full h-auto max-h-80 object-cover"
                  />
                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all"></div>
                  
                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-blue-500 group-hover:bg-blue-600 group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-2xl">
                      <Play className="w-8 h-8 text-white ml-1" fill="white" />
                    </div>
                  </div>
                  
                  {/* Watch Now text */}
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <p className="text-white text-base font-semibold drop-shadow-lg">
                      Click to Watch Demo
                    </p>
                  </div>
                </div>
              </div>

              {/* Video description */}
              <div className="mt-4 text-center">
                <p className="text-gray-400 text-sm">
                  See how NFC technology makes networking instant and effortless
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Video Modal */}
        <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
          <DialogContent className="max-w-5xl w-full p-0 bg-black border-zinc-800">
            <div className="relative aspect-video">
              <video
                className="w-full h-full"
                controls
                autoPlay
                preload="metadata"
              >
                <source 
                  src="https://video.gumlet.io/6743a0c58c65b60f3d3e06dc/6879a6ef0e8590900e004649/download.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </DialogContent>
        </Dialog>
      </section>

      {/* Social Proof */}
      <section className="py-24 bg-zinc-900 overflow-visible" data-animate>
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-16">
              Trusted by Professionals Worldwide
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="bg-gradient-to-br from-zinc-800 to-zinc-900 border-zinc-700 hover:border-blue-500/50 transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                      ))}
                    </div>
                    <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                        {testimonial.avatar}
                      </div>
                      <div>
                        <div className="font-bold text-white">{testimonial.name}</div>
                        <div className="text-sm text-gray-400">{testimonial.role}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-24 bg-black" data-animate>
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-16">
              Traditional Cards vs NFC
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <ScrollReveal>
              <Card className="bg-zinc-900 border-zinc-800 h-full">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-400 mb-6">Traditional Business Cards</h3>
                  <ul className="space-y-4">
                    {[
                      'Paper cards get lost or damaged',
                      'No engagement analytics',
                      'One-time use information',
                      'Costly to reprint',
                      'Manual data entry required',
                      'Limited information space'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-400">
                        <span className="text-red-500 mt-1">✕</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/50 h-full">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">NFC Smart Products</h3>
                  <ul className="space-y-4">
                    {[
                      'NFC never expires or degrades',
                      'Track every engagement',
                      'Reusable forever - update anytime',
                      'No reprinting ever needed',
                      'Instant contact saving',
                      'Unlimited information & links'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-white">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Design Your Own CTA */}
      <section id="custom" className="py-24 bg-gradient-to-br from-zinc-900 via-black to-zinc-900 relative overflow-hidden" data-animate>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-transparent to-blue-500/10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-500 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Palette className="w-4 h-4" />
                NEW: Live Design Tool
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Design Your Perfect NFC Solution
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                See your branding come to life with our interactive design tool. Upload your logo, choose colors, and preview exactly what you'll receive.
              </p>
              <Button 
                size="lg" 
                onClick={() => navigate('/customize')}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-lg px-10 py-7 group"
              >
                <Palette className="mr-2 w-5 h-5" />
                Try Design Tool Now
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { title: 'Upload Your Logo', description: 'Drag and drop your company logo' },
                { title: 'Choose Colors', description: 'Match your brand colors perfectly' },
                { title: 'Live Preview', description: 'See exactly what you\'ll receive' }
              ].map((feature, index) => (
                <Card key={index} className="bg-zinc-900/50 border-zinc-800 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-blue-500">{index + 1}</span>
                    </div>
                    <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                    <p className="text-gray-400 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-500" data-animate>
        <div className="container mx-auto px-6 text-center">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Upgrade Your Networking Today
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of professionals who've ditched paper cards for instant digital connections
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => navigate('/products')}
                className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6"
              >
                Shop NFC Solutions
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => navigate('/customize')}
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              >
                <Palette className="mr-2 w-5 h-5" />
                Design Your Own
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-zinc-900" data-animate>
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-16">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} delay={index * 50}>
                <Card className="bg-zinc-800 border-zinc-700 hover:border-blue-500/50 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-white mb-3">{faq.question}</h3>
                    <p className="text-gray-400">{faq.answer}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-black relative overflow-hidden" data-animate>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent"></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <ScrollReveal>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Start Sharing With One Tap
            </h2>
            <p className="text-2xl text-gray-400 mb-12">
              Transform how you network. Forever.
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/products')}
              className="bg-blue-500 hover:bg-blue-600 text-white text-xl px-12 py-8"
            >
              Shop Now
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-zinc-800 py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Wifi className="w-6 h-6 text-blue-500" />
                ShopNFCTags
              </h3>
              <p className="text-gray-400 text-sm">
                Premium NFC products for modern professionals
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <button 
                    onClick={() => navigate('/contact')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <button 
                    onClick={() => navigate('/refunds-returns')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Returns
                  </button>
                </li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <button 
                    onClick={() => navigate('/privacy-policy')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Privacy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/terms-of-service')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Terms
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 text-center text-gray-400 text-sm">
            © 2026 ShopNFCTags. All rights reserved.
          </div>
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
};

export default HomePage;
